import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Bike, CalendarRange, IdCard, CheckCircle2, KeyRound, Wind, RotateCcw,
  MessageCircle, LifeBuoy, Map, Compass, Sparkles, ArrowRight,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { BookingDialog } from "../components/BookingDialog";
import { MotoCard } from "../components/MotoCard";
import { Reveal, RevealStagger, revealItem } from "../components/Reveal";
import { whatsappLink } from "../data/site";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";

const STEP_ICONS = [Bike, CalendarRange, IdCard, CheckCircle2, KeyRound, Wind, RotateCcw];
const EXTRA_ICONS = [Compass, LifeBuoy, Map, CalendarRange];

const HERO_BG = "/images/gallery/fb-beach-bikes.jpg";

export default function ReserveNow() {
  const { t } = useLang();
  const [motos, setMotos] = useState([]);

  useEffect(() => {
    api.get("/motorcycles").then((r) => setMotos(r.data)).catch(() => {});
  }, []);

  const available = motos.filter((m) => m.available).slice(0, 3);
  const r = t.reservePage;

  const DepositButtons = ({ className = "" }) => (
    <div className={`flex flex-col sm:flex-row sm:flex-wrap gap-3 ${className}`}>
      {[25, 50].map((amt) => (
        <motion.a
          key={amt}
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.97 }}
          href={whatsappLink(t.wa.deposit(amt))}
          target="_blank"
          rel="noreferrer"
          data-testid={`reserve-deposit-${amt}-button`}
          className="inline-flex items-center justify-center w-full sm:w-auto gap-2 bg-[#25D366] hover:bg-[#1da851] text-white font-heading font-bold py-3 px-6 rounded-full transition-colors shadow-lg"
        >
          <MessageCircle size={18} /> ${amt}.00 {t.common.deposit}
        </motion.a>
      ))}
    </div>
  );

  return (
    <div className="App">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Reserve a moto" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-blue/95 via-brand-blue/80 to-brand-blue/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 w-full">
          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-brand-sky font-heading font-bold uppercase tracking-[0.2em] text-sm">
              <Sparkles size={16} /> {r.tag}
            </span>
            <h1 className="font-heading font-black text-white text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">{r.title}</h1>
            <p className="text-white/90 text-lg mt-5 max-w-xl leading-relaxed">{r.intro}</p>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3 mt-8">
              <BookingDialog
                trigger={
                  <motion.button whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.97 }} data-testid="reserve-hero-cta"
                    className="inline-flex items-center justify-center w-full sm:w-auto gap-2 bg-brand-orange hover:bg-brand-orange/90 text-white font-heading font-bold py-3 px-7 rounded-full transition-colors shadow-lg">
                    <CalendarRange size={18} /> {r.reserveCta}
                  </motion.button>
                }
              />
              <DepositButtons />
            </div>
          </motion.div>
        </div>
      </section>

      {/* STEPS TIMELINE */}
      <section className="py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-16">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{r.tag}</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2">{r.stepsTitle}</h2>
          </Reveal>

          <div className="relative">
            <div className="absolute left-[27px] sm:left-1/2 top-0 bottom-0 w-0.5 bg-brand-sky/30 sm:-translate-x-1/2" />
            <RevealStagger className="space-y-8">
              {r.steps.map((s, i) => {
                const Icon = STEP_ICONS[i] || Bike;
                const left = i % 2 === 0;
                return (
                  <motion.div key={i} variants={revealItem} className={`relative flex items-start gap-5 sm:gap-0 ${left ? "sm:flex-row" : "sm:flex-row-reverse"}`}>
                    <div className="relative z-10 shrink-0 sm:absolute sm:left-1/2 sm:-translate-x-1/2">
                      <div className="w-14 h-14 rounded-full bg-brand-blue text-white flex items-center justify-center shadow-lg ring-4 ring-white">
                        <Icon size={22} />
                      </div>
                    </div>
                    <div className={`sm:w-1/2 ${left ? "sm:pr-14 sm:text-right" : "sm:pl-14"}`}>
                      <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-shadow">
                        <span className="font-heading font-black text-brand-orange text-sm">{String(i + 1).padStart(2, "0")}</span>
                        <h3 className="font-heading font-bold text-lg text-brand-gray mt-1">{s.title}</h3>
                        <p className="text-slate-500 text-sm mt-2 leading-relaxed">{s.desc}</p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </RevealStagger>
          </div>
        </div>
      </section>

      {/* EXTRAS */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.services.tag}</span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2">{r.extrasTitle}</h2>
          </Reveal>
          <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {r.extras.map((e, i) => {
              const Icon = EXTRA_ICONS[i] || Compass;
              return (
                <motion.div key={i} variants={revealItem} whileHover={{ y: -8 }}
                  className="bg-white border-t-4 border-brand-orange rounded-2xl p-7 shadow-sm hover:shadow-xl transition-shadow">
                  <div className="w-12 h-12 rounded-full bg-brand-orange/10 text-brand-orange flex items-center justify-center mb-4">
                    <Icon size={24} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-brand-gray">{e.title}</h3>
                  <p className="text-slate-500 text-sm mt-2 leading-relaxed">{e.desc}</p>
                </motion.div>
              );
            })}
          </RevealStagger>
        </div>
      </section>

      {/* PICK A BIKE */}
      {available.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
              <div>
                <span className="text-sm font-bold tracking-[0.2em] uppercase text-brand-sky">{t.collection.tag}</span>
                <h2 className="font-heading font-black text-3xl sm:text-4xl text-brand-gray mt-2">{t.collection.title}</h2>
              </div>
            </Reveal>
            <RevealStagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {available.map((m) => (
                <motion.div key={m.id} variants={revealItem}><MotoCard moto={m} /></motion.div>
              ))}
            </RevealStagger>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="relative bg-brand-gray text-white py-20 overflow-hidden">
        <Reveal className="max-w-4xl mx-auto px-4 text-center relative z-10">
          <h2 className="font-heading font-black text-3xl sm:text-4xl">{r.ctaTitle}</h2>
          <p className="text-white/80 mt-3">{r.ctaSubtitle}</p>
          <div className="flex flex-col sm:flex-row sm:flex-wrap justify-center gap-3 mt-7">
            <BookingDialog
              trigger={
                <motion.button whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.97 }} data-testid="reserve-cta-button"
                  className="inline-flex items-center justify-center w-full sm:w-auto gap-2 bg-brand-orange hover:bg-brand-orange/90 text-white font-heading font-bold py-3 px-7 rounded-full transition-colors shadow-lg">
                  <CalendarRange size={18} /> {r.reserveCta}
                </motion.button>
              }
            />
            <DepositButtons />
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
